import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import {
	type Area,
	type DiningTable,
	type GeneratedFile,
	type TablesList,
	type UpsertTableInput,
	upsertTableInputSchema,
	uuidv7,
} from "@luckymenu/core";
import type { FormErrors } from "../menu/form-errors";

/**
 * More > Tables and QR codes, shared by both apps: tables grouped by area,
 * the next label to suggest, the table form, and refusals. Pure.
 */

export type AreaGroup = {
	/** Null: tables in no area, listed last. */
	area: Area | null;
	tables: DiningTable[];
};

/** Areas in order with their tables, then tables in no area. Empty areas are kept. */
export function tablesByArea(list: TablesList): AreaGroup[] {
	const groups: AreaGroup[] = list.areas.map((area) => ({
		area,
		tables: list.tables.filter((t) => t.areaId === area.id),
	}));
	const known = new Set(list.areas.map((a) => a.id));
	const loose = list.tables.filter((t) => t.areaId === null || !known.has(t.areaId));
	if (loose.length > 0 || list.areas.length === 0) groups.push({ area: null, tables: loose });
	return groups;
}

/**
 * A label for the next table: one more than the highest number among
 * `tables` (the area's, or all), with the same prefix. "T4" → "T5", "12" →
 * "13", nothing yet → "T1". Skips labels that are taken anywhere.
 */
export function nextTableLabel(
	tables: readonly DiningTable[],
	all: readonly DiningTable[] = tables,
) {
	const taken = new Set(all.map((t) => t.label.toLowerCase()));
	let prefix = "T";
	let highest = 0;
	for (const t of tables) {
		const match = /^(.*?)(\d+)$/.exec(t.label.trim());
		if (!match) continue;
		const n = Number(match[2]);
		if (n >= highest) {
			highest = n;
			prefix = match[1] ?? "";
		}
	}
	let n = highest + 1;
	while (taken.has(`${prefix}${n}`.toLowerCase())) n++;
	return `${prefix}${n}`;
}

/** A `data:` URL to show a generated QR code or file. */
export function generatedFileUrl(file: GeneratedFile): string {
	return `data:${file.mime};base64,${file.base64}`;
}

export type TableDraft = {
	id: string;
	label: string;
	/** Null: in no area. */
	areaId: string | null;
	/** Seats as typed. Empty: not given. */
	seats: string;
	isActive: boolean;
};

export function newTableDraft(label: string, areaId: string | null): TableDraft {
	return { id: uuidv7(), label, areaId, seats: "", isActive: true };
}

export function tableToDraft(table: DiningTable): TableDraft {
	return {
		id: table.id,
		label: table.label,
		areaId: table.areaId,
		seats: table.seats === null ? "" : String(table.seats),
		isActive: table.isActive,
	};
}

export function tableDraftToInput(
	draft: TableDraft,
): { ok: true; input: UpsertTableInput } | { ok: false; errors: FormErrors } {
	const errors: FormErrors = {};
	const label = draft.label.trim();
	if (label === "") errors.label = "required";
	else if (label.length > 20) errors.label = "tooLong";
	let seats: number | null = null;
	if (draft.seats.trim() !== "") {
		const n = Number(draft.seats.trim());
		if (!Number.isInteger(n) || n < 1 || n > 100) errors.seats = "invalid";
		else seats = n;
	}
	if (Object.keys(errors).length > 0) return { ok: false, errors };
	const input: UpsertTableInput = {
		id: draft.id,
		label,
		areaId: draft.areaId,
		seats,
		isActive: draft.isActive,
	};
	const parsed = upsertTableInputSchema.safeParse(input);
	if (!parsed.success) return { ok: false, errors: { label: "invalid" } };
	return { ok: true, input };
}

/** Why a tables change was refused. Apps translate `staff:tables.problems.<problem>`. */
export type TablesProblem =
	| "taken"
	| "areaNotEmpty"
	| "noTables"
	| "notFound"
	| "forbidden"
	| "network"
	| "failed";

export function tablesProblemOf(error: unknown): TablesProblem {
	const code = errorCodeOf(error);
	if (code === "NETWORK") return "network";
	if (code === "FORBIDDEN") return "forbidden";
	if (code === "NOT_FOUND") return "notFound";
	if (code === "CONFLICT") {
		const reason = errorDetailsOf(error).reason;
		if (reason === "taken") return "taken";
		if (reason === "area_not_empty") return "areaNotEmpty";
		if (reason === "no_tables") return "noTables";
	}
	return "failed";
}
